// src/components/EnviarAtaEmailModal.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { FaXmark, FaAlignLeft, FaListCheck, FaFileLines } from "react-icons/fa6";
import AtaPdf from "./AtaPdf";
import "./EnviarAtaEmailModal.css";

// ✅ Logo hospedada no Cloudinary (URL pública)
const ONBOX_LOGO_URL = "https://res.cloudinary.com/dmecovtmm/image/upload/v1768829076/onbox/ob5.jpg";

export default function EnviarAtaEmailModal({
  ataId,
  notaNome,
  projetoNome,
  participantes,
  dadosAta, // { dataLocal, pauta, local, texto } — necessário para regenerar o PDF
  onClose,
}) {
  const [destinatarios, setDestinatarios] = useState([]); // [{ id, nome, email, selecionado }]
  const [loading, setLoading] = useState(true);
  const [assunto, setAssunto] = useState(`Ata: ${notaNome}`);

  // ✅ Lista de nomes dos participantes para exibir no corpo do e-mail
  const nomesParticipantes = (participantes || [])
    .map(p => p.nomeCompleto || p.nome)
    .filter(Boolean);

  // ✅ Mensagem automática com logo ONBOX (URL pública Cloudinary) e nomes dos participantes
  const mensagemPadrao = `
<table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
  <tr>
    <td style="text-align: center; padding: 16px;">
      <img src="${ONBOX_LOGO_URL}" alt="ONBOX" style="height: 40px; margin-bottom: 12px;" />
    </td>
  </tr>
  <tr>
    <td style="padding: 16px; font-family: Arial, sans-serif; color: #333;">
      <p>Olá,</p>
      <p>Segue em anexo a ata da reunião "<strong>${notaNome}</strong>" referente ao projeto "<strong>${projetoNome}</strong>".</p>
      <p style="font-size: 12px; color: #666; margin-top: 8px;">
        <strong>Detalhes da Ata:</strong><br/>
        Data: ${dadosAta?.dataLocal || "—"}<br/>
        Local: ${dadosAta?.local || "—"}<br/>
        Participantes (${nomesParticipantes.length}): ${nomesParticipantes.join(", ") || "—"}
      </p>
      <p style="margin-top: 16px;">Atenciosamente,<br/><strong>ONBOX</strong></p>
      <hr style="border: none; border-top: 1px solid #ddd; margin: 2px 0;" />
      <p style="font-size: 11px; color: #999; margin: 0;">
        ONBOX | Plataforma de Gestão e Operações da Construção Civil <br/>
        <a href="https://onboxapp.com.br" style="color: #999; text-decoration: none;">https://onboxapp.com.br</a>
        fale conosco: <a href="mailto:contato@onboxapp.com.br" style="color: #999; text-decoration: none;">contato@onboxapp.com.br</a>
      </p>
    </td>
  </tr>
</table>
`;

  const [mensagem, setMensagem] = useState(mensagemPadrao);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // { ok: [], falha: [] }
  const [opcaoPdf, setOpcaoPdf] = useState("completo");

  useEffect(() => {
    const carregarEmails = async () => {
      setLoading(true);
      const internos = participantes.filter(p => !String(p.id).startsWith("ext-"));
      const externos = participantes.filter(p => String(p.id).startsWith("ext-"));

      let emailsInternos = {};
      if (internos.length) {
        const { data } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", internos.map(p => p.id));
        emailsInternos = (data || []).reduce((acc, p) => { acc[p.id] = p.email; return acc; }, {});
      }

      const lista = [
        ...internos.map(p => ({
          id: p.id,
          nome: p.nomeCompleto || p.nome,
          email: emailsInternos[p.id] || "",
          externo: false,
          selecionado: !!emailsInternos[p.id],
        })),
        ...externos.map(p => ({
          id: p.id,
          nome: p.nomeCompleto || p.nome,
          email: p.emailExterno || "",
          externo: true,
          selecionado: !!p.emailExterno,
        })),
      ];

      setDestinatarios(lista);
      setLoading(false);
    };
    carregarEmails();
  }, [participantes]);

  const toggleSelecionado = (id) => {
    setDestinatarios(prev => prev.map(d => d.id === id ? { ...d, selecionado: !d.selecionado } : d));
  };

  const atualizarEmailExterno = (id, email) => {
    setDestinatarios(prev => prev.map(d => d.id === id ? { ...d, email } : d));
  };

  const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");

  const salvarEmailExternoNoBanco = async (participanteId, email) => {
    if (!String(participanteId).startsWith("ext-")) return;
    const realId = String(participanteId).replace("ext-", "");
    if (!validarEmail(email)) return;
    await supabase.from("ata_participantes").update({ email_externo: email }).eq("id", realId);
  };

  const buscarObjetivos = async () => {
  if (!ataId) return [];

  const { data: od, error } = await supabase
    .from("ata_objetivos")
    .select("*")
    .eq("ata_id", ataId)
    .order("ordem", { ascending: true })
    .order("id", { ascending: true });

  if (error) { console.error(error); return []; }
  if (!od?.length) return [];

  const ids = od.map(o => o.id);

  const { data: rd } = await supabase
    .from("ata_objetivos_responsaveis_enriquecidos")
    .select("*")
    .in("ata_objetivo_id", ids);

  const respPorObj = (rd || []).reduce((acc, r) => {
    if (!acc[r.ata_objetivo_id]) acc[r.ata_objetivo_id] = [];
    acc[r.ata_objetivo_id].push({
      id: r.id,
      usuario_id: r.usuario_id,
      nome_externo: r.nome_externo,
      nome_exibicao: r.nome_exibicao,
    });
    return acc;
  }, {});

  const { data: comentariosData } = await supabase
    .from("ata_objetivos_comentarios")
    .select("*")
    .in("ata_objetivo_id", ids)
    .order("created_at", { ascending: true });

  const userIdsComentarios = [...new Set((comentariosData || []).map(c => c.usuario_id))];
  let nomesMap = {};
  if (userIdsComentarios.length) {
    const { data: profilesComentarios } = await supabase
      .from("profiles")
      .select("id, nome")
      .in("id", userIdsComentarios);
    nomesMap = (profilesComentarios || []).reduce((acc, p) => { acc[p.id] = p.nome; return acc; }, {});
  }

  const comentariosPorObj = (comentariosData || []).reduce((acc, c) => {
    if (!acc[c.ata_objetivo_id]) acc[c.ata_objetivo_id] = [];
    acc[c.ata_objetivo_id].push({
      autor: nomesMap[c.usuario_id] || "Usuário",
      texto: c.comentario,
      data: c.created_at,
    });
    return acc;
  }, {});

  return od
    .filter(o => !o.texto?.startsWith('[EXCLUIDO]'))
    .map(o => ({
      id: o.id,
      texto: o.texto || "",
      responsaveis: respPorObj[o.id] || [],
      dataEntrega: o.data_entrega,
      concluido: o.concluido || false,
      concluidoEm: o.concluido_em ? new Date(o.concluido_em) : null,
      comentario: o.comentario || "",
      comentarios: comentariosPorObj[o.id] || [],
    }));
};

  const enviar = async () => {
    const selecionados = destinatarios.filter(d => d.selecionado);
    if (!selecionados.length) { alert("Selecione ao menos um destinatário."); return; }
    const semEmail = selecionados.filter(d => !validarEmail(d.email));
    if (semEmail.length) {
      alert(`Preencha um e-mail válido para: ${semEmail.map(d => d.nome).join(", ")}`);
      return;
    }
    if (!ataId) {
      alert("Salve a ata antes de enviar por e-mail.");
      return;
    }

    setEnviando(true);
    setResultado(null);
    try {
      let objetivos = [];
      if (opcaoPdf === "objetivos" || opcaoPdf === "completo") {
        objetivos = await buscarObjetivos();
        if (!objetivos.length && opcaoPdf !== "texto") {
          const continuar = window.confirm("Nenhum objetivo encontrado. Deseja enviar só com o texto?");
          if (!continuar) { setEnviando(false); return; }
        }
      }

      const pdfBase64 = await AtaPdf.gerarBase64(
        opcaoPdf, projetoNome, notaNome, dadosAta.dataLocal,
        dadosAta.pauta, dadosAta.local, dadosAta.texto, participantes, objetivos, dadosAta.observacao || ''
      );

      const ok = [];
      const falha = [];

      for (const d of selecionados) {
        try {
          const { data, error } = await supabase.functions.invoke("enviar-ata-email", {
            body: {
              to: [d.email],
              subject: assunto,
              html: mensagem.replace(/\n/g, "<br/>"),
              pdfBase64,
              pdfFileName: `Ata - ${notaNome}.pdf`,
            },
          });
          if (error || data?.error) throw new Error(data?.error || error.message);
          ok.push(d.nome);
        } catch (err) {
          falha.push({ nome: d.nome, motivo: err.message });
        }
      }

      setResultado({ ok, falha });
    } catch (err) {
      alert(`Erro ao gerar PDF ou enviar: ${err.message}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="enviar-ata-email-modal__overlay">
      <div className="enviar-ata-email-modal">
        <div className="enviar-ata-email-modal__header">
          <h3>Enviar ata por e-mail</h3>
          <button className="enviar-ata-email-modal__close" onClick={onClose}>
            <FaXmark size={14} />
          </button>
        </div>

        {loading ? (
          <div className="enviar-ata-email-modal__loading">Carregando participantes...</div>
        ) : resultado ? (
          <div className="enviar-ata-email-modal__resultado">
            {resultado.ok.length > 0 && (
              <p className="enviar-ata-email-modal__ok">
                ✅ Enviado com sucesso para: {resultado.ok.join(", ")}
              </p>
            )}
            {resultado.falha.length > 0 && (
              <div className="enviar-ata-email-modal__falha">
                <p>❌ Falha ao enviar para:</p>
                <ul>
                  {resultado.falha.map((f, i) => <li key={i}>{f.nome} — {f.motivo}</li>)}
                </ul>
              </div>
            )}
            <button className="enviar-ata-email-modal__fechar-btn" onClick={onClose}>Fechar</button>
          </div>
        ) : (
          <>
            <div className="enviar-ata-email-modal__body">
              <div className="enviar-ata-email-modal__field">
                <label>Assunto</label>
                <input type="text" value={assunto} onChange={e => setAssunto(e.target.value)} />
              </div>

              <div className="enviar-ata-email-modal__field">
                <label>Mensagem</label>
                <textarea rows={6} value={mensagem} onChange={e => setMensagem(e.target.value)} />
                <small style={{ color: "#999", marginTop: "4px" }}>
                  💡 A logo ONBOX e os nomes dos participantes já estão incluídos automaticamente na mensagem
                </small>
              </div>

              <div className="enviar-ata-email-modal__field">
                <label>Conteúdo do PDF</label>
                <div className="enviar-ata-email-modal__opcoes-pdf">
                  <button
                    type="button"
                    className={`enviar-ata-email-modal__opcao-card ${opcaoPdf === "texto" ? "ativo" : ""}`}
                    onClick={() => setOpcaoPdf("texto")}
                  >
                    <FaAlignLeft size={18} />
                    <span>Apenas texto</span>
                  </button>
                  <button
                    type="button"
                    className={`enviar-ata-email-modal__opcao-card ${opcaoPdf === "objetivos" ? "ativo" : ""}`}
                    onClick={() => setOpcaoPdf("objetivos")}
                  >
                    <FaListCheck size={18} />
                    <span>Apenas objetivos</span>
                  </button>
                  <button
                    type="button"
                    className={`enviar-ata-email-modal__opcao-card ${opcaoPdf === "completo" ? "ativo" : ""}`}
                    onClick={() => setOpcaoPdf("completo")}
                  >
                    <FaFileLines size={18} />
                    <span>Texto e objetivos</span>
                  </button>
                </div>
              </div>

              <div className="enviar-ata-email-modal__field">
                <label>Destinatários</label>
                {destinatarios.length === 0 && (
                  <p className="enviar-ata-email-modal__empty">Nenhum participante na ata.</p>
                )}
                <div className="enviar-ata-email-modal__lista">
                  {destinatarios.map(d => (
                    <div key={d.id} className="enviar-ata-email-modal__item">
                      <input
                        type="checkbox"
                        checked={d.selecionado}
                        onChange={() => toggleSelecionado(d.id)}
                      />
                      <span className="enviar-ata-email-modal__nome">{d.nome}</span>
                      {d.externo ? (
                        <input
                          type="email"
                          placeholder="email@exemplo.com"
                          value={d.email}
                          onChange={e => atualizarEmailExterno(d.id, e.target.value)}
                          onBlur={e => salvarEmailExternoNoBanco(d.id, e.target.value)}
                          className="enviar-ata-email-modal__email-input"
                        />
                      ) : (
                        <span className="enviar-ata-email-modal__email-fixo">
                          {d.email || "sem e-mail cadastrado"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="enviar-ata-email-modal__footer">
              <button className="enviar-ata-email-modal__cancelar" onClick={onClose} disabled={enviando}>
                Cancelar
              </button>
              <button className="enviar-ata-email-modal__enviar" onClick={enviar} disabled={enviando}>
                {enviando ? "Enviando..." : "Enviar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}